/**
 * Dreier-Modus — an wen eine Nachricht geht, als FELD statt als geratenes Satzmuster. (16.10.2026, Stufe 1)
 *
 * Bis hierher riet eine Textregel (shared/schmied.ts, `anSchmiedGerichtet`/`sprichtSchmiedAn`), wer angesprochen ist — und lag
 * mehrfach daneben (Martin: „Okay, wunderbar … Frage dezidiert an Schmidt …“ → Vespera antwortete trotzdem). Martin, 16.10. 07:13:
 * Ja zu „Stufe 1“ — ein festes Adressatenfeld `an`:
 *   - der Entwickler (scripts/dreier-sagen.mjs `--an martin|vespera|schmied`) trägt es an seinem Beitrag;
 *   - Martin wählt es neben „Senden“ („An: Vespera | Schmied“, frontend/lib/empfaenger-wahl.ts), Chat wie Sprechtaste.
 * Das Feld hat Vorrang; ohne Feld gilt das Satzmuster wie bisher (Rückfall). Ein ungültiger Wert zählt wie kein Feld.
 *
 * EIN Besitzer für die erlaubten Werte und ihre Prüfung — Server (Route, Weiche, Faden) und Seite rechnen damit gleich.
 */

/** Der Name des Feldes im Rumpf (Beitrag, Chat), im Formular der Sprechtaste, in `metadata` und auf dem Bus. */
export const ADRESSAT_FELD = "an";

export const ADRESSATEN = ["martin", "vespera", "schmied", "zimmermann", "hueter"] as const;
export type Adressat = (typeof ADRESSATEN)[number];

/** Ein gültiger Adressat — sonst `null` (fehlt, falscher Typ, unbekannt). Groß/klein und Leerraum zählen nicht. Rein. */
export function adressatAus(wert: unknown): Adressat | null {
  if (typeof wert !== "string" || wert.length > 20) return null;
  const w = wert.trim().toLowerCase();
  return (ADRESSATEN as readonly string[]).includes(w) ? (w as Adressat) : null;
}

/** Der zweite Adressat eines Beitrags: gültig und nicht derselbe wie der erste — sonst `null`. Ohne ersten keiner. Rein. */
export function sekundaerAus(wert: unknown, primaer: Adressat | null): Adressat | null {
  const s = adressatAus(wert);
  return primaer && s && s !== primaer ? s : null;
}

/**
 * Was Martin wählen kann: Vespera, den Schmied oder den Zimmermann (sich selbst nicht).
 * 17.10.2026 (Martin 15:08): „Hey Zimmermann, das betrifft dich“ — der Zimmermann ist ein eigener Adressat. Er gilt NUR bei
 * eingeschaltetem „Zimmermann im Chat“ (Konto 1) und offenem Notschalter; sonst bleibt die Wahl wirkungslos (server/adressat-jev.ts).
 * 17.10.2026 (Martin 19:46): ebenso der Hüter (Server 7) — „Hüter im Chat“ und `ENTWICKLER_HUETER=1`.
 */
export type MartinsEmpfaenger = Extract<Adressat, "vespera" | "schmied" | "zimmermann" | "hueter">;

/** Martins Wahl aus einem Rumpf- oder Formularfeld — `null`, wenn sie fehlt oder ungültig ist („martin“ eingeschlossen). Rein. */
export function martinsWahlAus(wert: unknown): MartinsEmpfaenger | null {
  const a = adressatAus(wert);
  return a === "vespera" || a === "schmied" || a === "zimmermann" || a === "hueter" ? a : null;
}

/**
 * Ticket 3296 (17.10.2026): ein Schalter wie „--an martin“ gehört nie in den Text. Rutscht er als erstes in den Beitrag
 * (z. B. alles in Anführungszeichen übergeben), zeigt ihn die Blase und das Vorlesen sagt „Bindestrich Bindestrich an Martin“.
 * Entfernt NUR führende `--an X` / `--sekundaer X` (auch `=X`, mehrfach) mit gültigem Adressaten; nie mitten im Text. Rein.
 */
export function ohneFuehrendenAdressatMarker(text: string): string {
  const muster = new RegExp(`^\\s*--(?:an|sekundaer)(?:\\s+|=)(?:${ADRESSATEN.join("|")})(?![\\p{L}\\p{N}_-])[,:]?\\s*`, "iu");
  let rest = text;
  for (let i = 0; i < 4 && muster.test(rest); i++) rest = rest.replace(muster, "");
  return rest === text ? text : rest.trim();
}
